import React from 'react';
import { Card, Row, Col, Button, Empty } from 'antd';

import { AreaChart, AreaChartSeriesItem, colorUtilService } from '../shared/charts';

import { useGetCheckpointTrendsQuery } from '../../graphql/dataQuality.generated';
import { DataQualityTrendsResponse } from '../../types.generated';
import { useTranslation } from '../../i18n/useTranslation';
import { EXPORT_HIDE_ATTRIBUTE, EXPORT_LOADING_ATTRIBUTE } from './captureDashboardSections';
import { WIDGET_LOADING_STYLE } from './utils';
import { postMessageService, postMessageAction } from '../shared/postMessageService';
import { mergeDashboardFilters, useDashboardFilters } from './dashboardFilters';

import { ReactComponent as OpenInNewIcon } from '../../images/icon-open-in-new.svg';

const formatMonthLabel = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

export const DataQualityTrendWidget = () => {
    const { t } = useTranslation();
    const { dataQualityColorMap } = colorUtilService;
    const dashboardFilters = useDashboardFilters();

    const { data, previousData, loading } = useGetCheckpointTrendsQuery({
        variables: {
            input: {
                period: 'year',
                orFilters: mergeDashboardFilters(dashboardFilters),
            },
        },
        fetchPolicy: 'no-cache',
    });

    /**
     * While the filter-driven refetch is in flight Apollo sets data=undefined.
     * Fall back to the last successful response so the chart stays visible
     * and only the loading indicator signals that a reload is happening.
     */
    const effectiveData = data ?? previousData;

    const handleOpenCheckpointListing = () => {
        postMessageService.sendToParent({
            action: postMessageAction.OPEN_CHECKPOINT_LISTING_PAGE,
        });
    };

    const trendData = effectiveData?.getCheckpointTrends?.trend_data ?? [];
    const metadata = effectiveData?.getCheckpointTrends?.metadata as DataQualityTrendsResponse['metadata'];

    const categories = trendData.map((d) => formatMonthLabel(d.timestamp));

    const series: AreaChartSeriesItem[] = [
        {
            name: t('Passed'),
            color: dataQualityColorMap.Passed,
            data: trendData.map((d) => d.passed_count),
        },
        {
            name: t('Failed'),
            color: dataQualityColorMap.Failed,
            data: trendData.map((d) => d.failed_count),
        },
    ];

    const passedPercentage =
        (metadata?.overall_pass_rate || 0) === 0 ? '0%' : `${metadata?.overall_pass_rate?.toFixed(1)}%`;

    const renderContent = () => {
        if (loading && trendData.length === 0) {
            // Marked so the PDF export waits for the widget to finish loading before capturing.
            return (
                <div style={WIDGET_LOADING_STYLE} {...{ [EXPORT_LOADING_ATTRIBUTE]: 'true' }}>
                    {t('Loading')}
                </div>
            );
        }
        if (trendData.length === 0) {
            return (
                <div style={WIDGET_LOADING_STYLE}>
                    <Empty description={t('NO_DATA_AVAILABLE')} />
                </div>
            );
        }
        return (
            <>
                <div className="f-left-border-highlight f-color-securiti-green py-0 mb-3">
                    <Row gutter={8} align="middle">
                        <Col flex="none">
                            <div className="f-text-big-title f-text-single-line f-text-semibold f-color-dark-black-s100">
                                {passedPercentage}
                            </div>
                        </Col>
                        <Col flex="none">
                            <div className="f-text-medium-content f-text-single-line f-text-semibold f-color-dark-black-s70">
                                {t('Tests Passed')}
                            </div>
                        </Col>
                    </Row>
                </div>
                <AreaChart series={series} categories={categories} height={250} showLegend={false} />
            </>
        );
    };

    return (
        <Card
            className="f-bordered f-dashboard-grid-card"
            title={
                <Row align="middle" className="f-card-header f-gap-1">
                    <Col flex="none">
                        <div className="f-card-title">{t('Data Quality - Checkpoint Score')}</div>
                    </Col>
                    <Col flex="auto" {...{ [EXPORT_HIDE_ATTRIBUTE]: 'true' }}>
                        <Button
                            type="text"
                            size="small"
                            icon={<OpenInNewIcon />}
                            title={t('Open in New Tab')}
                            aria-label={t('Open in New Tab')}
                            className="icon-button ma-0"
                            onClick={handleOpenCheckpointListing}
                        />
                    </Col>
                </Row>
            }
        >
            {renderContent()}
        </Card>
    );
};
